/**
 * Calendar constants and color palette
 */

// Event colors available for selection (from legacy system)
export const CALENDAR_COLORS = [
	{ id: 'indigo', color: '#3f51b5', label: 'Indigo' },
	{ id: 'lightblue', color: '#039be5', label: 'Světle modrá' },
	{ id: 'lavender', color: '#7986cb', label: 'Levandule' },
	{ id: 'grey', color: '#616161', label: 'Šedá' },
	{ id: 'red', color: '#d50000', label: 'Červená' },
	{ id: 'orange', color: '#f4511e', label: 'Oranžová' },
	{ id: 'salmon', color: '#e67c73', label: 'Lososová' },
	{ id: 'purple', color: '#8e24aa', label: 'Fialová' },
	{ id: 'green', color: '#0b8043', label: 'Zelená' },
	{ id: 'teal', color: '#33b679', label: 'Tyrkysová' },
	{ id: 'yellow', color: '#f6bf26', label: 'Žlutá' },
] as const

// Status-specific background colors (RGB format from original app)
export const STATUS_BACKGROUNDS = {
	CANCELLED: 'rgb(97, 97, 97)',
	NO_SHOW: 'rgb(213, 0, 0)',
	LUNCH: '#33b679',
	URGENT: '#f6bf26',
} as const

// Default color for new events
export const DEFAULT_EVENT_COLOR = '#3f51b5'

// Dummy patient ID used for events without real patients
export const DUMMY_PATIENT_ID = '000000/0000'

// Calendar time boundaries
export const CALENDAR_CONFIG = {
	MIN_HOUR: 6, // 6:00 AM
	MAX_HOUR: 20, // 8:00 PM
	STEP_MINUTES: 15, // 15-minute intervals
	TIMESLOTS_PER_HOUR: 4,
	AUTO_REFRESH_INTERVAL: 60000, // 60 seconds
} as const

// Appointment types
export const APPOINTMENT_TYPES = {
	ZUB: 'ZUB',
	DH: 'DH',
	OTHER: 'OTHER',
} as const

export type AppointmentType = keyof typeof APPOINTMENT_TYPES

// Action types for ZUB calendar
export const ZUB_ACTION_TYPES = [
	{ value: 'pp', label: 'PP' },
	{ value: 'akut', label: 'Akut' },
	{ value: 'endo', label: 'ENDO' },
	{ value: 'brouseni', label: 'Broušení' },
	{ value: 'ftk', label: 'FTK' },
	{ value: 'otisky', label: 'Otisky' },
	{ value: 'opg', label: 'OPG' },
	{ value: 'frc', label: 'FRC' },
	{ value: 'ex', label: 'EX' },
	{ value: 'plomba_bila', label: 'Plomba bílá' },
	{ value: 'jine', label: 'Jiné' },
] as const

// Action types for DH calendar
export const DH_ACTION_TYPES = [
	{ value: 'sgo', label: 'SGO' },
	{ value: 'la', label: 'LA' },
	{ value: 'recall', label: 'Recall' },
	{ value: 'kontrola_po_sgo', label: 'Kontrola po SGO' },
	{ value: 'vstupni', label: 'Vstupní ošetření' },
	{ value: 'jine', label: 'Jiné' },
] as const

/**
 * Get action types based on calendar type
 */
export function getActionTypes(calendarType: AppointmentType) {
	switch (calendarType) {
		case 'ZUB':
			return ZUB_ACTION_TYPES
		case 'DH':
			return DH_ACTION_TYPES
		default:
			return []
	}
}

// View modes
export const VIEW_MODES = {
	week: 'Týden',
	work_week: 'Pracovní týden',
	day: 'Den',
} as const

export type ViewMode = keyof typeof VIEW_MODES

/**
 * Check if a background color indicates a cancelled appointment (gray)
 */
export function isCancelledBackground(background: string | null): boolean {
	if (!background) return false
	return (
		background === STATUS_BACKGROUNDS.CANCELLED ||
		background === STATUS_BACKGROUNDS.NO_SHOW ||
		background === '#616161' ||
		background === '#d50000'
	)
}

/**
 * Check if a background color is specifically cancelled (gray), not no-show (red)
 */
export function isCancelledOnly(background: string | null): boolean {
	if (!background) return false
	return (
		background === STATUS_BACKGROUNDS.CANCELLED ||
		background === '#616161'
	)
}

/**
 * Check if a background color is no-show (red)
 */
export function isNoShowBackground(background: string | null): boolean {
	if (!background) return false
	return (
		background === STATUS_BACKGROUNDS.NO_SHOW ||
		background === '#d50000'
	)
}

/**
 * Get contrasting text color for a background
 */
export function getContrastColor(background: string): string {
	// Light colors that need dark text
	const lightColors = ['#f6bf26', '#e67c73']
	if (lightColors.includes(background.toLowerCase())) {
		return '#1a1a1a'
	}
	// All other calendar colors work well with white text
	return '#ffffff'
}
